<i18n locale="en">
{
  "title": "Photo",
  "labels": {
    "upload": "Drag & drop or choose a photo to upload",
    "select": "Select Photo",
    "link": "Click-through URL"
  },
  "placeholders": {
    "link": "https://example.com/landing-page"
  },
  "validation": {
    "insecureProtocol": "Please use a secure link starting with https://",
    "invalidUrl": "Please enter a valid URL (e.g., https://example.com)"
  }
}
</i18n>

<template>
  <EditorLayout>
    <div class="flex flex-col gap-8">
      <OwnInputContainer :label="t('title')">
        <ImageInput
          v-model="image"
          :label="t('labels.select')"
          :upload-label="t('labels.upload')"
          :crop-width="700"
        />
      </OwnInputContainer>
      <OwnInputContainer
        v-if="linkablePhotoEnabled"
        :label="t('labels.link')"
        :errors="linkErrors"
      >
        <OwnInput v-model="link" :placeholder="t('placeholders.link')" />
      </OwnInputContainer>
    </div>

    <template #actions>
      <slot :save-disabled="linkablePhotoEnabled && !isLinkValid" />
    </template>
  </EditorLayout>
</template>

<script>
import debounce from 'lodash/debounce'
import isURL from 'validator/lib/isURL'
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import { ImageInput } from '@/components/inputs/ImageInput'
import EditorLayout from '@/lib/builder/public/editor/EditorLayout.vue'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import { ImageCommands } from '../commands/ImageCommands'

export default {
  name: 'ImageEditor',
  components: { EditorLayout, ImageInput, OwnInput, OwnInputContainer },
  props: {
    /** ID of the block */
    id: { type: String, required: true },
  },
  setup() {
    const { t } = useI18n()
    const { enabled: linkablePhotoEnabled } = useReactiveFeatureFlag(
      'hermes.emailBuilder.linkablePhoto'
    )

    return { linkablePhotoEnabled, t }
  },
  data() {
    return {
      image: null,
      link: '',
    }
  },
  computed: {
    ...mapGetters({
      blockById: 'emailBuilder/blockById',
    }),
    currentImage() {
      const { id } = this

      return this.blockById(id)?.data?.image
    },
    currentLink() {
      const { id } = this

      return this.blockById(id)?.data?.link ?? ''
    },
    isLinkValid() {
      return this.linkErrors.length === 0
    },
    linkErrors() {
      const { link, t } = this

      if (!link) return []

      if (link.startsWith('http://')) {
        return [t('validation.insecureProtocol')]
      }

      if (
        !isURL(link, {
          protocols: ['https'],
          require_protocol: true,
          require_valid_protocol: true,
        })
      ) {
        return [t('validation.invalidUrl')]
      }

      return []
    },
  },
  watch: {
    async image(image) {
      await ImageCommands.updateBlock({
        targetId: this.id,
        update: { data: { image } },
      })
    },
    link(link) {
      if (link && link.includes('.') && !link.includes('://')) {
        this.link = `https://${link}`

        return
      }

      if (this.isLinkValid) {
        this.debouncedSaveLink(link)
      }
    },
  },
  created() {
    this.debouncedSaveLink = debounce(
      async function (link) {
        await ImageCommands.updateBlock({
          targetId: this.id,
          update: { data: { link: link || null } },
        })
      }.bind(this),
      500
    )
  },
  mounted() {
    this.image = this.currentImage
    this.link = this.currentLink
  },
  beforeUnmount() {
    this.debouncedSaveLink.flush()
  },
}
</script>
